<template>
<link v-if="isUmbraco" rel="stylesheet" href="/assets/index.css" />

  <div ref="scrollElement" :class="containerStyles">
    <div :class="[strokeStyles, 'mb-[60px]']" ref="strokeElement" />
    <rich-text
      :text="text"
      :isUmbraco="isUmbraco"
      :theme="themeType"
      isManchet
      classes="col-start-2 col-span-12 md:col-start-4 md:col-span-8 [&_p]:font-bold"
    />
    <paragraph
      v-if="author"
      classes="font-bold col-start-2 col-span-12 md:col-start-4 md:col-span-8 mt-8 mb-0 text-sm"
      :is-umbraco="isUmbraco"
      :theme="themeType"
      :text="author"
    />
    <paragraph
      v-if="profession"
      classes="col-start-2 col-span-12 md:col-start-4 md:col-span-8 mb-0 text-sm"
      :is-umbraco="isUmbraco"
      :theme="themeType"
      :text="profession"
    />
    <div :class="[strokeStyles, 'mt-[60px]']" ref="strokeElement" />
  </div>
</template>

<script lang="ts" setup>
import RichText from "@components/Typography/RichText.ce.vue";
import Paragraph from "@components/Typography/Paragraph.ce.vue";
import { IQuoteProps } from "@/types/components/IQuoteProps";
import { computed, onMounted, toRefs, ref, Ref, onUnmounted, watch } from "vue";
import { changeTheme, getThemeType } from "@/util/helper";
import { onIntersect } from "@/composables/onIntersect";

const props = withDefaults(defineProps<IQuoteProps>(), {
  isUmbraco: false,
  theme: "sand",
});

const { text, author, profession, isUmbraco, theme } = toRefs(props);

const observer: Ref<IntersectionObserver | null> = ref(null);
const scrollElement = ref(null);
const animateIn = ref(false);
const strokeElement: Ref<HTMLDivElement | null> = ref(null);

const onEnter = () => {
  if (theme.value) {
    changeTheme(theme.value);
  }
};
const onExit = () => {};

const onLineEnter = () => {
  animateIn.value = true;
};
const onLineExit = () => {};

onMounted(() => {
  observer.value = onIntersect(scrollElement.value, onEnter, onExit, false, {
    threshold: 0.1,
  });

  observer.value = onIntersect(
    scrollElement.value,
    onLineEnter,
    onLineExit,
    false,
    {
      threshold: 0.8,
    }
  );
});

onUnmounted(() => {
  if (!observer.value) return;
  observer.value.disconnect();
});

watch(theme, (value) => {
  if (!value) return;
  changeTheme(value);
});

const themeType = computed(() => {
  return getThemeType(theme.value);
});

const containerStyles = computed(() => {
  const bgColor = isUmbraco.value ? `bg-${theme.value}` : ``;
  return `${bgColor} sectionContainer md:min-h-full py-10 lg:py-[60px]`;
});

const strokeStyles = computed(() => {
  const pos = animateIn.value
    ? "after:translate-x-0"
    : "after:translate-x-[-100%]";
  return `h-3 overflow-hidden col-start-2 col-span-8 md:col-start-4 md:col-span-3  relative after:transition after:duration-500 after:ease-accelerate after:content-[""] after:h-full after:w-full after:absolute after:bg-line after:left-0 after:top-0 ${pos}`;
});
</script>
